// AboutPage.js
import React from 'react';
import { Box, Container, Typography, Paper, useTheme } from '@mui/material';

export default function AboutPage() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Style for the feature cards (matching HowToUsePage styling)
  const paperSx = {
    p: 3,
    textAlign: 'center',
    color: theme.palette.text.primary,
    backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
    boxShadow: theme.shadows[3],
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
  };

  // Feature data array similar to steps in HowToUsePage
  const features = [
    {
      title: 'Claim API',
      description:
        'Our proprietary Claim API scans and analyzes claims from diverse sources, assigning a reliability score so you can quickly gauge accuracy.',
    },
    {
      title: 'Wikipedia API',
      description:
        'By tapping into Wikipedia’s vast repository, we fetch relevant articles and historical context to give you a deeper understanding of each claim.',
    },
    {
      title: 'DeepSeek AI',
      description:
        'Our DeepSeek AI synthesizes data from multiple sources to generate human-like insights and explanations. We recommend cross-checking the results for best accuracy.',
    },
  ];

  return (
    <Box
      sx={{
        py: 8,
        backgroundColor: isDarkMode ? '#121212' : '#f5f5f5',
        minHeight: '100vh',
      }}
    >
      <Container maxWidth="md">
        {/* Header Section */}
        <Typography variant="body1" component="h1" align="center" gutterBottom>
        <img
      src="../xfactualLogo.png"
      alt="XFactual Logo"
      style={{ width: "200px", height: "auto" }}
    />
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          Discover the power behind our advanced fact-checking platform.
        </Typography>
        <Typography variant="body1" align="center" paragraph>
          Fact Checker leverages cutting-edge technology to help you verify claims quickly and confidently.
          Our platform integrates multiple APIs and advanced AI systems to deliver comprehensive insights on every claim.
        </Typography>

        {/* Features Section in a Grid Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: 4,
            mt: 4,
          }}
        >
          {features.map((feature, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Paper sx={paperSx}>
                <Typography variant="h5" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2">
                  {feature.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        {/* Additional Information Section */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h4" align="center" gutterBottom>
            What Else Can You Do?
          </Typography>
          <Typography variant="body1" align="center" paragraph>
            • Quickly verify trending claims with just a few clicks. <br />
            • Explore detailed context, links, and supporting data for every claim. <br />
            • Experience our fast, intuitive interface designed for seamless fact-checking. <br />
            • Stay informed with the latest insights powered by our advanced integrations.
          </Typography>
        </Box>

        {/* Call to Action */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Ready to experience a new era of fact-checking?
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Join us today and see for yourself how Fact Checker is revolutionizing the way you verify information.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
